// Parked Orders — dedicated CS-facing page for un-parking stuck orders.
//
// Different from OrdersDashboard: this is a triage view built around the
// core CS question — "why is this parked, how long has it been sitting,
// and what's the next action?"
//
// Layout:
//   [ Header · KPIs (parked count · £ at risk · escalated · chased today) ]
//   [ LEFT rail: reason buckets · age buckets · escalated toggle ]
//   [ RIGHT: rich table with age, reason, last-chased, last-note, per-row
//     inline actions: Unpark · Mark chased · Escalate · Open ]
//
// Bulk actions bar appears when 1+ rows are checked.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, Search, Package, PoundSterling, AlertTriangle, MessageSquare,
  Undo2, PhoneOutgoing, Flag, ExternalLink, RotateCcw, StickyNote,
} from "lucide-react";
import SyncButton from "../../components/SyncButton";
import InfiniteSentinel from "../../components/InfiniteSentinel";
import { AITimelineButton, AITimelineModal } from "./AIOrderTimeline";
import { OrderDetailModal } from "./OrdersDashboard";
import {
  API, token, money, fmtDate, fmtDateTime, TIER_TONE, HoverNote,
} from "./_shared";

const AGE_LABELS = { "0-3d": "0-3 days", "4-7d": "4-7 days", "8-14d": "8-14 days", "15d+": "15+ days", unknown: "No age" };
const REASON_TONES = {
  payment:  "text-orange-300 bg-orange-500/10 border-orange-500/40",
  supplier: "text-sky-300 bg-sky-500/10 border-sky-500/40",
  margin:   "text-amber-300 bg-amber-500/10 border-amber-500/40",
  customer: "text-fuchsia-300 bg-fuchsia-500/10 border-fuchsia-500/40",
  manual:   "text-slate-300 bg-slate-500/10 border-slate-500/40",
  unknown:  "text-slate-500 bg-white/[0.02] border-white/10",
};

export default function ParkedOrders() {
  const LIMIT = 50;
  const [items, setItems]                 = useState([]);
  const [total, setTotal]                 = useState(0);
  const [kpis, setKpis]                   = useState(null);
  const [reasons, setReasons]             = useState([]);
  const [reasonCounts, setReasonCounts]   = useState({});
  const [ageCounts, setAgeCounts]         = useState({});
  const [filters, setFilters]             = useState({ reason: "", age_bucket: "", include_escalated_only: false, q: "" });
  const [loading, setLoading]             = useState(true);
  const [hasMore, setHasMore]             = useState(true);
  const [selected, setSelected]           = useState(new Set());
  const [openId, setOpenId]               = useState(null);
  const [aiOrderId, setAiOrderId]         = useState(null);
  const [escalateFor, setEscalateFor]     = useState(null);   // order id being escalated
  const [escalateReason, setEscalateReason] = useState("");
  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const genRef = useRef(0);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchAt = useCallback(async (offset, append) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    const gen = genRef.current;
    const f = filtersRef.current;
    try {
      const params = new URLSearchParams({
        ...(f.reason && { reason: f.reason }),
        ...(f.age_bucket && { age_bucket: f.age_bucket }),
        ...(f.include_escalated_only && { include_escalated_only: "true" }),
        ...(f.q && { q: f.q }),
        limit: LIMIT, offset,
      });
      const r = await axios.get(`${API}/orders/parked?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (gen !== genRef.current) return;
      const batch = r.data.items || [];
      const t = r.data.total || 0;
      setItems((prev) => (append ? [...prev, ...batch] : batch));
      setTotal(t);
      setKpis(r.data.kpis || null);
      setReasons(r.data.reasons || []);
      setReasonCounts(r.data.reason_counts || {});
      setAgeCounts(r.data.age_counts || {});
      if (!append) setSelected(new Set());
      offsetRef.current = offset + batch.length;
      setHasMore(offset + batch.length < t);
    } catch (e) {
      if (gen === genRef.current) toast.error("Failed to load parked orders", { description: e?.response?.data?.detail || "" });
    } finally {
      if (gen === genRef.current) { loadingRef.current = false; setLoading(false); }
    }
  }, []);

  const load = useCallback(() => {
    genRef.current += 1;
    offsetRef.current = 0;
    loadingRef.current = false;
    setHasMore(true);
    fetchAt(0, false);
  }, [fetchAt]);

  const loadMore = useCallback(() => {
    if (!loadingRef.current && hasMore) fetchAt(offsetRef.current, true);
  }, [hasMore, fetchAt]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.reason, filters.age_bucket, filters.include_escalated_only]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [filters.q]);

  // ─── Per-row actions ───────────────────────────────────────────────────
  const post = async (id, path, body = {}, successMsg) => {
    try {
      await axios.post(`${API}/orders/${id}/${path}`, body, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success(successMsg);
      load();
    } catch (e) {
      toast.error("Action failed", { description: e?.response?.data?.detail || e.message });
    }
  };
  const unpark   = (id)   => post(id, "unpark", {}, "Unparked");
  const chased   = (id, target) => post(id, "mark-chased", { chased: target }, `Marked chased (${target})`);
  const escalate = (id, reason) => {
    // Bulk path passes an explicit reason; empty is rejected (guarded here + on backend).
    const r = (reason || "").trim();
    if (!r) return;
    post(id, "escalate", { reason: r }, "Escalated");
  };
  const unescalate = (id) => post(id, "unescalate", {}, "Un-escalated");
  const submitEscalate = async () => {
    const r = escalateReason.trim();
    if (!r) return;
    if (escalateFor === "__bulk__") {
      await bulkRun((id) => escalate(id, r));   // same typed reason across the batch
    } else {
      await post(escalateFor, "escalate", { reason: r }, "Escalated");
    }
    setEscalateFor(null); setEscalateReason("");
  };

  // ─── Bulk actions ──────────────────────────────────────────────────────
  const bulkRun = async (fn) => {
    if (selected.size === 0) return;
    const ids = [...selected];
    for (const id of ids) await fn(id);
    setSelected(new Set());
  };
  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((r) => r.id)));
  };

  const activeFilters = useMemo(() => filters.reason || filters.age_bucket || filters.include_escalated_only || filters.q, [filters]);
  const anySelectedEscalated = useMemo(() => items.some((r) => selected.has(r.id) && r.escalated), [items, selected]);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="parked-orders">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <AlertTriangle className="w-5 h-5 text-amber-300" />
        <h1 className="text-xl font-semibold text-white">Parked Orders</h1>
        <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
          Customer Service triage · unblock stalled orders
        </span>
        <div className="ml-auto flex items-center gap-2">
          <SyncButton resource="orders" />
          <button onClick={load} className="p-1.5 rounded hover:bg-white/[0.06] text-slate-400 hover:text-white"
            title="Reload" data-testid="parked-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* KPI strip */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="parked-kpis">
          <Kpi label="Parked"        value={kpis.parked_orders}         icon={Package}      tone="text-amber-300"/>
          <Kpi label="Value at risk" value={money(kpis.value_at_risk)}   icon={PoundSterling}tone="text-rose-300"/>
          <Kpi label="Escalated"     value={kpis.escalated}              icon={Flag}         tone="text-rose-300"/>
          <Kpi label="Chased today"  value={kpis.chased_today}           icon={PhoneOutgoing}tone="text-sky-300"/>
        </div>
      )}

      {/* Search + reset */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input type="text" placeholder="Search customer, email, supplier, reason…"
            value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="w-full pl-8 pr-3 py-1.5 rounded border border-white/10 bg-white/[0.02] text-slate-100 text-[13px] placeholder-slate-500 focus:border-amber-500/40 focus:outline-none"
            data-testid="parked-search"
          />
        </div>
        <label className="flex items-center gap-1.5 text-[11px] text-slate-300 cursor-pointer">
          <input type="checkbox" checked={filters.include_escalated_only}
            onChange={(e) => setFilters({ ...filters, include_escalated_only: e.target.checked })}
            className="accent-rose-400" data-testid="parked-escalated-toggle" />
          Escalated only
        </label>
        {activeFilters && (
          <button onClick={() => setFilters({ reason: "", age_bucket: "", include_escalated_only: false, q: "" })}
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded border border-rose-500/30 bg-rose-500/10 text-rose-200 text-[10.5px] font-mono uppercase tracking-widest hover:bg-rose-500/20"
            data-testid="parked-reset">
            <RotateCcw className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {/* Layout: LEFT rail (reason + age) + RIGHT table */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        {/* LEFT rail */}
        <div className="space-y-4" data-testid="parked-rail">
          <RailFilter title="Reason" items={reasons.map((r) => ({
            key: r.key, label: r.label, count: reasonCounts[r.key] || 0,
          }))} active={filters.reason} onPick={(k) => setFilters({ ...filters, reason: k })} testidPrefix="parked-reason" tones={REASON_TONES} />
          <RailFilter title="Age (since parked)" items={Object.entries(ageCounts).map(([k, v]) => ({
            key: k, label: AGE_LABELS[k] || k, count: v,
          }))} active={filters.age_bucket} onPick={(k) => setFilters({ ...filters, age_bucket: k })} testidPrefix="parked-age" />
        </div>

        {/* RIGHT — table */}
        <div className="rounded-lg border border-white/5 bg-white/[0.02] overflow-hidden" data-testid="parked-table">
          {selected.size > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 bg-emerald-500/[0.08]">
              <span className="text-[11px] font-mono text-emerald-200 uppercase tracking-widest">
                {selected.size} selected
              </span>
              <div className="ml-auto flex items-center gap-1.5">
                <BulkButton onClick={() => bulkRun(unpark)} icon={Undo2} tone="emerald" testid="parked-bulk-unpark">Unpark</BulkButton>
                <BulkButton onClick={() => bulkRun((id) => chased(id, "supplier"))} icon={PhoneOutgoing} tone="sky" testid="parked-bulk-chase">Chase supplier</BulkButton>
                <BulkButton onClick={() => { setEscalateReason(""); setEscalateFor("__bulk__"); }} icon={Flag} tone="rose" testid="parked-bulk-escalate">Escalate</BulkButton>
                {anySelectedEscalated && (
                  <BulkButton onClick={() => bulkRun(unescalate)} icon={RotateCcw} tone="amber" testid="parked-bulk-unescalate">Un-escalate</BulkButton>
                )}
              </div>
            </div>
          )}

          {/* Header row */}
          <div className="grid grid-cols-[28px_90px_1fr_120px_140px_100px_100px_120px_140px_190px] gap-2 px-3 py-2 text-[10px] font-mono text-slate-500 uppercase tracking-widest border-b border-white/5">
            <input type="checkbox" checked={selected.size === items.length && items.length > 0}
              onChange={toggleAll} className="accent-emerald-400" data-testid="parked-select-all" />
            <div>Order#</div><div>Customer</div><div>Account Mgr</div><div>Reason</div>
            <div>Age</div><div className="text-right">Value</div>
            <div>Supplier</div><div>Last note</div><div className="text-right">Actions</div>
          </div>

          {loading && items.length === 0 && <div className="text-center text-slate-500 py-8">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="text-center text-slate-500 py-10 text-[13px]">
              🎉 No parked orders match those filters. All hands on deck!
            </div>
          )}

          {items.map((o) => (
            <ParkedRow key={o.id} o={o}
              checked={selected.has(o.id)}
              onToggle={() => toggleSelect(o.id)}
              onOpen={() => setOpenId(o.id)}
              onAI={() => setAiOrderId(o.id)}
              onUnpark={() => unpark(o.id)}
              onChase={() => chased(o.id, window.prompt("Chased who?", "supplier") || "supplier")}
              onEscalate={() => { setEscalateReason(""); setEscalateFor(o.id); }}
              onUnescalate={() => unescalate(o.id)}
            />
          ))}
          {items.length > 0 && (
            <InfiniteSentinel
              onReach={loadMore}
              hasMore={hasMore}
              loading={loading}
              count={items.length}
              total={total}
              testId="parked-sentinel"
            />
          )}
        </div>
      </div>

      {openId && <OrderDetailModal orderId={openId} onClose={() => { setOpenId(null); load(); }} onOpenAI={() => setAiOrderId(openId)} />}
      {aiOrderId && <AITimelineModal orderId={aiOrderId} onClose={() => setAiOrderId(null)} />}

      {escalateFor && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60"
          onClick={() => setEscalateFor(null)} data-testid="parked-escalate-modal">
          <div className="w-[440px] max-w-[92vw] rounded-xl border border-rose-500/25 bg-[#0d1420] p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3 text-rose-300">
              <Flag className="w-4 h-4" />
              <span className="text-[11px] font-mono uppercase tracking-widest font-bold">
                {escalateFor === "__bulk__" ? `Escalate ${selected.size} order${selected.size === 1 ? "" : "s"}` : "Escalate order"}
              </span>
            </div>
            <label className="text-[11px] text-slate-400 mb-1.5 block">Escalation reason <span className="text-rose-400">*</span></label>
            <textarea
              autoFocus rows={3} value={escalateReason}
              onChange={(e) => setEscalateReason(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitEscalate(); }}
              placeholder="Why is this being escalated? (required)"
              className="w-full rounded-lg bg-black/30 border border-white/10 px-3 py-2 text-[13px] text-white outline-none focus:border-rose-400/60 resize-none"
              data-testid="parked-escalate-reason" />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button onClick={() => setEscalateFor(null)} data-testid="parked-escalate-cancel"
                className="px-3 py-1.5 rounded-lg text-[12px] text-slate-300 hover:bg-white/5 border border-white/10">Cancel</button>
              <button onClick={submitEscalate} disabled={!escalateReason.trim()} data-testid="parked-escalate-submit"
                className="px-3 py-1.5 rounded-lg text-[12px] font-semibold border border-rose-500/40 bg-rose-500/15 text-rose-200 hover:bg-rose-500/25 disabled:opacity-40 disabled:cursor-not-allowed">
                Escalate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Row ───────────────────────────────────────────────────────────────
function ParkedRow({ o, checked, onToggle, onOpen, onAI, onUnpark, onChase, onEscalate, onUnescalate }) {
  const reasonTone = REASON_TONES[o.reason_group] || REASON_TONES.unknown;
  const ageTone =
    o.age_bucket === "15d+" ? "text-rose-300"
    : o.age_bucket === "8-14d" ? "text-amber-300"
    : o.age_bucket === "4-7d" ? "text-sky-300"
    : "text-slate-400";

  return (
    <div className={`grid grid-cols-[28px_90px_1fr_120px_140px_100px_100px_120px_140px_190px] gap-2 px-3 py-2 border-b border-white/[0.03] items-center text-[12.5px] hover:bg-white/[0.03] ${o.escalated ? "bg-rose-500/[0.03]" : ""}`}
      data-testid={`parked-row-${o.id}`}
    >
      <input type="checkbox" checked={checked} onChange={onToggle} className="accent-emerald-400"
        onClick={(e) => e.stopPropagation()} data-testid={`parked-select-${o.id}`} />
      {/* Order # */}
      <div className="min-w-0">
        <div className="text-slate-100 font-mono text-[11px]">#{o.order_number}</div>
        <div className="text-[9.5px] font-mono text-slate-500 truncate">{fmtDate(o.order_date)}</div>
      </div>
      {/* Customer */}
      <div className="min-w-0 cursor-pointer" onClick={onOpen}>
        <div className="text-slate-100 truncate flex items-center gap-1.5">
          {o.customer_tier && (
            <span className={`text-[8.5px] font-mono px-1 py-0.5 rounded uppercase tracking-widest ${TIER_TONE[String(o.customer_tier).toUpperCase()] || "text-slate-400 bg-white/5"}`}>
              {String(o.customer_tier)[0]}
            </span>
          )}
          <span className="truncate">{o.customer_name}</span>
          {o.escalated && <Flag className="w-3 h-3 text-rose-400 shrink-0" />}
        </div>
        <div className="text-[10px] font-mono text-slate-500 truncate">{o.company || o.email || o.postcode || "—"}</div>
      </div>
      {/* Account Manager */}
      <div className="min-w-0" data-testid={`parked-am-${o.id}`}>
        {o.account_manager
          ? <span className="text-slate-200 truncate text-[11.5px] block">{o.account_manager}</span>
          : <span className="text-slate-600 text-[11px]">—</span>}
      </div>
      {/* Reason */}
      <div className="min-w-0">
        <span className={`inline-block text-[9.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${reasonTone}`}>{o.reason_group}</span>
        {o.parked_reason && <div className="text-[10px] text-slate-400 truncate mt-0.5" title={o.parked_reason}>{o.parked_reason}</div>}
      </div>
      {/* Age */}
      <div className="min-w-0">
        <div className={`text-[11.5px] font-mono ${ageTone}`}>{o.age_days != null ? `${o.age_days}d` : "—"}</div>
        <div className="text-[9px] font-mono text-slate-500 uppercase tracking-widest">{o.age_bucket}</div>
      </div>
      {/* Value */}
      <div className="min-w-0 text-right">
        <div className="text-slate-100 tabular-nums font-mono text-[12px]">{money(o.cust_price_inc_vat)}</div>
        {o.margin_nr != null && <div className={`text-[9.5px] font-mono ${o.margin_nr >= 20 ? "text-emerald-300" : "text-amber-300"}`}>{o.margin_nr}%</div>}
      </div>
      {/* Supplier */}
      <div className="min-w-0">
        <div className="text-slate-200 truncate text-[11.5px]">{o.supplier || "—"}</div>
        {o.supplier_branch && <div className="text-[10px] font-mono text-slate-500 truncate">{o.supplier_branch}</div>}
      </div>
      {/* Last note (hover-expand) */}
      <div className="min-w-0" onClick={(e) => e.stopPropagation()}>
        {o.last_system_note?.msg
          ? <HoverNote text={`${o.last_system_note.actor || ""}: ${o.last_system_note.msg}`} icon={StickyNote} />
          : <span className="text-slate-600 text-[10px] italic">—</span>}
        {o.last_chased_at && (
          <div className="text-[9px] font-mono text-sky-300 mt-0.5">chased {fmtDateTime(o.last_chased_at)}</div>
        )}
      </div>
      {/* Actions */}
      <div className="flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
        <IconBtn onClick={onUnpark}   icon={Undo2}         title="Unpark"     tone="emerald" testid={`parked-unpark-${o.id}`} />
        <IconBtn onClick={onChase}    icon={PhoneOutgoing} title="Mark chased"tone="sky"     testid={`parked-chase-${o.id}`} />
        {o.escalated
          ? <IconBtn onClick={onUnescalate} icon={RotateCcw} title="Un-escalate (keeps parked)" tone="amber" testid={`parked-unescalate-${o.id}`} />
          : <IconBtn onClick={onEscalate}   icon={Flag}      title="Escalate"                   tone="rose"  testid={`parked-escalate-${o.id}`} />}
        <AITimelineButton orderId={o.id} onOpen={onAI} />
        <IconBtn onClick={onOpen}     icon={ExternalLink}  title="Open detail"tone="slate"   testid={`parked-open-${o.id}`} />
      </div>
    </div>
  );
}

// ─── UI primitives ─────────────────────────────────────────────────────
function Kpi({ label, value, icon: Icon, tone }) {
  return (
    <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3 flex items-center gap-3">
      <Icon className={`w-6 h-6 ${tone}`} />
      <div>
        <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">{label}</div>
        <div className="text-lg font-semibold text-white tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function RailFilter({ title, items, active, onPick, testidPrefix, tones = {} }) {
  return (
    <div>
      <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
        <MessageSquare className="w-3 h-3" /> {title}
      </div>
      <div className="space-y-1">
        <button onClick={() => onPick("")}
          className={`w-full text-left px-2 py-1.5 rounded border text-[11.5px] flex justify-between items-center ${
            active === "" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                          : "border-white/10 text-slate-400 hover:bg-white/[0.04]"}`}
          data-testid={`${testidPrefix}-all`}
        >
          <span>All</span>
          <span className="text-[10px] font-mono text-slate-500">{items.reduce((a, b) => a + b.count, 0)}</span>
        </button>
        {items.map((it) => (
          <button key={it.key} onClick={() => onPick(it.key)}
            className={`w-full text-left px-2 py-1.5 rounded border text-[11.5px] flex justify-between items-center ${
              active === it.key
                ? (tones[it.key] || "border-emerald-500/40 bg-emerald-500/10 text-emerald-200")
                : "border-white/10 text-slate-400 hover:bg-white/[0.04]"}`}
            data-testid={`${testidPrefix}-${it.key}`}
          >
            <span className="truncate">{it.label}</span>
            <span className="text-[10px] font-mono text-slate-500 ml-2">{it.count}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function BulkButton({ onClick, icon: Icon, tone, children, testid }) {
  const cls =
    tone === "emerald" ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25"
    : tone === "sky"   ? "border-sky-500/40 bg-sky-500/15 text-sky-200 hover:bg-sky-500/25"
    : tone === "rose"  ? "border-rose-500/40 bg-rose-500/15 text-rose-200 hover:bg-rose-500/25"
    : tone === "amber" ? "border-amber-500/40 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25"
    :                    "border-white/10 text-slate-300 hover:bg-white/[0.06]";
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-[10.5px] font-mono uppercase tracking-widest ${cls}`}>
      <Icon className="w-3 h-3" /> {children}
    </button>
  );
}

function IconBtn({ onClick, icon: Icon, title, tone, testid }) {
  const cls =
    tone === "emerald" ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15"
    : tone === "sky"   ? "border-sky-500/40 text-sky-300 hover:bg-sky-500/15"
    : tone === "rose"  ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/15"
    : tone === "amber" ? "border-amber-500/40 text-amber-300 hover:bg-amber-500/15"
    :                    "border-white/10 text-slate-400 hover:bg-white/[0.06] hover:text-white";
  return (
    <button onClick={onClick} title={title} data-testid={testid}
      className={`p-1 rounded border ${cls}`}>
      <Icon className="w-3 h-3" />
    </button>
  );
}
